import React from "react";
import { Award, Info } from "lucide-react";
import { PrimeLadder } from "../PrimeLadder";
import { fmtPct, fmtDate, roleLabel, GOLD } from "@/lib/prime";
import { PrimeLevel } from "../PrimeLevel";

function Stat({ label, value, testId, gold }) {
  return (
    <div className="rounded-xl bg-white/10 px-4 py-3" data-testid={testId}>
      <p className="text-[11px] uppercase tracking-wider text-[#5EEAD4]">{label}</p>
      <p className="text-lg sm:text-2xl font-bold mt-0.5 break-words" style={gold ? { color: GOLD } : undefined}>{value}</p>
    </div>
  );
}

function AgencyScore({ a, multi }) {
  const c = a.current;
  return (
    <div className="space-y-4" data-testid={`growth-score-${a.business_id}`}>
      {(multi || a.role === "staff") && (
        <p className="text-sm text-white/80"><b className="text-white">{a.business_name}</b> · {roleLabel(a.role)}
          {a.role === "staff" && <span className="ml-2 text-[11px] rounded-full bg-white/15 px-2 py-0.5">Agency result — not an individual score</span>}</p>)}
      {!c ? (
        <p className="rounded-xl bg-white/10 p-4 text-sm" data-testid="growth-no-result">Your Prime result has not been added yet. Your district team adds it from Farmers' Prime reporting. Everything below is still available to help you plan.</p>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Stat label="Achievement Level" value={c.level ? <PrimeLevel level={c.level} className={c.level === "PRIME" ? "!text-[#C9A227]" : "!text-white"} /> : "Not recorded"} testId="growth-level" gold={c.level === "PRIME"} />
            <Stat label="Prime Score" value={fmtPct(c.score_pct) || "Not recorded"} testId="growth-score" />
            <Stat label="Reporting month" value={c.period_label} testId="growth-period" />
            <Stat label="Source" value={<span className="text-sm font-medium">{c.source}<span className="block text-[11px] text-white/60">Updated {fmtDate(c.updated_at)}</span></span>} testId="growth-source" />
          </div>
          <div className="rounded-xl bg-white p-3"><PrimeLadder index={c.ladder_index} /></div>
          {(c.achieved_points !== null && c.achieved_points !== undefined) && (
            <p className="text-sm text-white/90" data-testid="growth-points"><b className="text-white">{Number(c.achieved_points).toFixed(2)}</b> of {Number(c.available_points).toFixed(2)} possible KPI points{c.pif_band ? ` · PIF Band ${c.pif_band}` : ""}{c.tenure ? ` · Tenure ${c.tenure}` : ""}</p>)}
          {c.level === "PRIME" ? (
            <p className="text-sm text-white/90" data-testid="growth-distance"><Award className="w-4 h-4 inline mr-1" style={{ color: GOLD }} />You're at PRIME. The next step is keeping growth balanced across metrics so your result holds month after month.</p>
          ) : c.pp_to_next !== null && c.pp_to_next !== undefined ? (
            <p className="text-sm text-white/90" data-testid="growth-distance"><b className="text-white">{c.pp_to_next.toFixed(1)} percentage points</b> to {c.next_level}, based on your {c.period_label} result.</p>
          ) : (
            <p className="text-xs text-white/70" data-testid="growth-distance">The distance to the next level isn't shown because this result has no Prime Score percentage, or the published level differs from its score band.</p>)}
        </>)}
    </div>
  );
}

export function ScoreHero({ agencies, intro, rulesYear }) {
  return (
    <section className="rounded-3xl bg-[#013A5A] text-white p-5 sm:p-8 relative overflow-hidden" data-testid="growth-hero">
      <svg aria-hidden className="absolute -right-16 -top-16 w-72 h-72 opacity-20" viewBox="0 0 200 200"><circle cx="100" cy="100" r="90" fill="none" stroke="#5EEAD4" strokeWidth="2" /><circle cx="100" cy="100" r="60" fill="none" stroke={GOLD} strokeWidth="2" /></svg>
      <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold relative" data-testid="growth-title">Your Path to PRIME</h1>
      <p className="mt-2 text-base md:text-lg text-[#5EEAD4] font-medium relative">Build your team. Strengthen your systems. Invest in your agency's future.</p>
      <p className="mt-3 text-sm text-white/80 max-w-3xl relative">{intro}</p>
      <div className="mt-6 space-y-6 relative">
        {agencies.length === 0 ? <p className="rounded-xl bg-white/10 p-4 text-sm" data-testid="growth-no-result">Your Prime result has not been added yet. No Agency relationship is linked to your account. Ask your district team to link your Agency.</p>
          : agencies.map((a) => <AgencyScore key={a.business_id} a={a} multi={agencies.length > 1} />)}
      </div>
      <p className="mt-6 text-sm text-white/90 flex gap-2 relative"><Info className="w-4 h-4 shrink-0 mt-0.5 text-[#5EEAD4]" />Your Prime Score reflects the percentage of available KPI points achieved. PRIME begins at 70%—you do not need to maximize every metric to reach it.</p>
      <p className="mt-1 text-[11px] text-white/60 relative">{rulesYear} achievement bands. Farmers' Prime reporting is the official record.</p>
    </section>
  );
}
